import React, { memo, useState } from 'react';
import { Conditional, Text, Space, Loader } from '@cashfree-intl/coherent';

// Constants
import {
  DOMAIN_REF,
  WHITELIST_INITIAL_FORM_DATA,
  TEST_IDS,
  DOMAIN_TYPE,
} from '../constants';
import { SOURCE_TYPE, TEST } from 'utils/constants';
import { getAddToWhitelistSchema } from '../validation';

// Components
import WhitelistStepOne from './WhitelistStepOne';
import WhitelistStepTwo from './WhitelistStepTwo';
import FormikComponent from 'components/Formik';

// Styled
import { StyledAddToWhitelistModal, StyledStepChip } from '../styled';

// Types
import {
  AddToWhitelistModalProps,
  WhiteListFormikValuesType,
  WhiteListValuesType,
} from '../types';
import { useSearchParams } from 'react-router-dom';

const AddToWhitelistModal = ({
  openReviewModal,
  onModalClose,
  openInactiveModal,
  type,
  addWhitelisting,
  checkLiveness,
  isLoading,
}: AddToWhitelistModalProps) => {
  const [searchParams] = useSearchParams();
  const [stepNumber, setStepNumber] = useState(0);
  const navigateToStepTwo = () => setStepNumber(1);
  const navigateToStepOne = () => setStepNumber(0);
  const isTest = searchParams.get('env') === TEST;
  const isFirstStep = stepNumber === 0;
  const confirmText = isFirstStep ? 'Proceed' : 'Confirm & Proceed';
  const isWebsite = type === DOMAIN_REF;
  const domainType = isWebsite ? DOMAIN_TYPE.WEBSITE : DOMAIN_TYPE.APP;
  const sourceType = SOURCE_TYPE.DASHBOARD;
  return (
    <FormikComponent
      initialValues={WHITELIST_INITIAL_FORM_DATA}
      validationSchema={getAddToWhitelistSchema(isWebsite, isTest)}
      onSubmit={(values: WhiteListValuesType, actions) => {
        if (isWebsite) {
          checkLiveness({
            reqData: {
              url: values.channelValue,
            },
            successCb: isLive => {
              if (isLive) navigateToStepTwo();
              else
                openInactiveModal({
                  domainUrl: values.channelValue,
                  websiteLoginDetails: values.loginAvailable
                    ? values.testCredentials
                    : undefined,
                });
            },
          });
        } else navigateToStepTwo();
        actions.setSubmitting(false);
      }}
    >
      {(formikProps: WhiteListFormikValuesType) => {
        const {
          values,
          isValid,
          isValidating,
          isSubmitting,
          handleSubmit,
        } = formikProps;
        const disableSubmitButton = !isValid || (!isValidating && isSubmitting);
        const onConfirm = () => {
          if (isFirstStep) {
            handleSubmit();
          } else {
            addWhitelisting({
              reqData: {
                domainUrl: values.channelValue,
                domainType,
                websiteLoginDetails: values.loginAvailable
                  ? values.testCredentials
                  : undefined,
                sourceType,
                isWebsiteLive: true,
              },
              successCb: res => openReviewModal(res, isWebsite),
              errorCb: onModalClose,
            });
          }
        };
        return (
          <StyledAddToWhitelistModal
            onClose={onModalClose}
            onConfirm={onConfirm}
            confirmText={confirmText}
            confirmButtonProps={{ disabled: disableSubmitButton }}
            title={
              <Space alignItems="center">
                <Text variant="h20" className="mr-1">
                  {isWebsite ? 'Website URL' : 'App Link'}
                </Text>
                <StyledStepChip variant="b12" color="white">
                  Step {stepNumber + 1}/2
                </StyledStepChip>
              </Space>
            }
            data-testid={TEST_IDS.ADD_TO_WHITELIST_MODAL}
          >
            <Loader active={isLoading} page={false} />
            <Conditional if={isFirstStep}>
              <WhitelistStepOne
                isWebsite={isWebsite}
                isTest={isTest}
                formikProps={formikProps}
              />
            </Conditional>
            <Conditional if={!isFirstStep}>
              <WhitelistStepTwo
                onBackClick={navigateToStepOne}
                isWebsite={isWebsite}
              />
            </Conditional>
          </StyledAddToWhitelistModal>
        );
      }}
    </FormikComponent>
  );
};

export default memo(AddToWhitelistModal);
